import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MembershipPlanResponse, MembershipResponse, CompanyResponse } from '../../../../../../@shared/models/response.module';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface PlanSelection {
  plan: MembershipPlanResponse;
  selected: boolean;
  memberCount: number;
}

@Component({
  selector: 'app-export-members-modal',
  standalone: false,
  templateUrl: './export-members-modal.html',
  styleUrl: './export-members-modal.css'
})
export class ExportMembersModal implements OnInit {
  @Input() plans: MembershipPlanResponse[] = [];
  @Input() memberships: MembershipResponse[] = [];
  @Input() company: CompanyResponse | null = null;

  planSelections: PlanSelection[] = [];
  exportFormat: 'csv' | 'pdf' = 'pdf';
  includeExpired: boolean = false;
  exporting: boolean = false;

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit() {
    this.planSelections = this.plans.map(plan => ({
      plan,
      selected: true,
      memberCount: this.getMemberCountForPlan(plan.plan_id)
    }));
  }

  getMemberCountForPlan(planId: number): number {
    return this.memberships
      .filter(m => m.plan?.plan_id === planId)
      .reduce((count, m) => count + this.getMembershipMemberCount(m), 0);
  }

  /**
   * Get the actual number of members for a membership.
   * For family memberships, counts all family members.
   */
  getMembershipMemberCount(membership: MembershipResponse): number {
    if (membership.family && membership.family.members && membership.family.members.length > 0) {
      return membership.family.members.length;
    }
    return 1;
  }

  get selectedPlansCount(): number {
    return this.planSelections.filter(ps => ps.selected).length;
  }

  get totalSelectedMembers(): number {
    const selectedPlanIds = this.planSelections
      .filter(ps => ps.selected)
      .map(ps => ps.plan.plan_id);

    return this.memberships
      .filter(m => m.plan && selectedPlanIds.includes(m.plan.plan_id))
      .reduce((sum, m) => sum + this.getMembershipMemberCount(m), 0);
  }

  toggleAll(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.planSelections.forEach(ps => ps.selected = checked);
  }

  get allSelected(): boolean {
    return this.planSelections.every(ps => ps.selected);
  }

  exportMembers() {
    this.exporting = true;

    const selectedPlanIds = this.planSelections
      .filter(ps => ps.selected)
      .map(ps => ps.plan.plan_id);

    let membersToExport = this.memberships.filter(m =>
      m.plan && selectedPlanIds.includes(m.plan.plan_id)
    );

    if (!this.includeExpired) {
      membersToExport = membersToExport.filter(m => m.status !== 'expired');
    }

    if (this.exportFormat === 'pdf') {
      this.exportPDF(membersToExport);
    } else {
      this.exportCSV(membersToExport);
    }

    this.exporting = false;
    this.activeModal.close(true);
  }

  /**
   * Expand memberships to include all family members as individual rows.
   * Family memberships become multiple rows (one per family member).
   */
  expandMembershipsToMembers(memberships: MembershipResponse[]): Array<{
    name: string;
    email: string;
    phone: string;
    plan: string;
    coverage: string;
    status: string;
    paymentStatus: string;
    startedAt: Date | null;
    expiresAt: Date | null;
    ownerType: string;
    familyRole?: string;
  }> {
    const expandedMembers: Array<{
      name: string;
      email: string;
      phone: string;
      plan: string;
      coverage: string;
      status: string;
      paymentStatus: string;
      startedAt: Date | null;
      expiresAt: Date | null;
      ownerType: string;
      familyRole?: string;
    }> = [];

    for (const membership of memberships) {
      // If it's a family membership with members, expand to individual rows
      if (membership.family && membership.family.members && membership.family.members.length > 0) {
        for (const familyMember of membership.family.members) {
          const user = familyMember.user;
          expandedMembers.push({
            name: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || `Member`,
            email: user.email || '',
            phone: user.phone || '',
            plan: membership.plan?.name || '',
            coverage: membership.plan?.coverage || '',
            status: membership.status,
            paymentStatus: membership.payment_status,
            startedAt: membership.started_at,
            expiresAt: membership.expires_at,
            ownerType: 'family',
            familyRole: familyMember.role
          });
        }
      } else {
        // Individual or manual membership - single row
        expandedMembers.push({
          name: this.getMemberName(membership),
          email: this.getMemberEmail(membership),
          phone: this.getMemberPhone(membership),
          plan: membership.plan?.name || '',
          coverage: membership.plan?.coverage || '',
          status: membership.status,
          paymentStatus: membership.payment_status,
          startedAt: membership.started_at,
          expiresAt: membership.expires_at,
          ownerType: membership.owner_type
        });
      }
    }

    return expandedMembers;
  }

  exportPDF(memberships: MembershipResponse[]) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Expand family memberships to individual members
    const expandedMembers = this.expandMembershipsToMembers(memberships);

    // Header
    doc.setFontSize(20);
    doc.setTextColor(54, 153, 255);
    doc.text('Membership Report', pageWidth / 2, 20, { align: 'center' });

    // Company name if available
    if (this.company?.company_name) {
      doc.setFontSize(14);
      doc.setTextColor(100, 100, 100);
      doc.text(this.company.company_name, pageWidth / 2, 28, { align: 'center' });
    }

    // Date
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated: ${new Date().toLocaleDateString('nb-NO')}`, pageWidth / 2, 35, { align: 'center' });

    // Summary stats (now based on expanded members)
    const activeCount = expandedMembers.filter(m => m.status === 'active').length;
    const pendingCount = expandedMembers.filter(m => m.status === 'pending').length;
    const expiredCount = expandedMembers.filter(m => m.status === 'expired').length;

    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total: ${expandedMembers.length}  |  Active: ${activeCount}  |  Pending: ${pendingCount}  |  Expired: ${expiredCount}`, pageWidth / 2, 45, { align: 'center' });

    // Table
    const tableData = expandedMembers.map(m => [
      m.name,
      m.email,
      m.phone,
      m.plan || '-',
      m.status.charAt(0).toUpperCase() + m.status.slice(1),
      this.formatDate(m.startedAt),
      this.formatDate(m.expiresAt)
    ]);

    autoTable(doc, {
      head: [['Name', 'Email', 'Phone', 'Plan', 'Status', 'Started', 'Expires']],
      body: tableData,
      startY: 55,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [54, 153, 255],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 248, 250],
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 40 },
        2: { cellWidth: 25 },
        3: { cellWidth: 30 },
        4: { cellWidth: 20 },
        5: { cellWidth: 22 },
        6: { cellWidth: 22 },
      },
    });

    // Footer on each page
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    doc.save(`members_export_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  exportCSV(memberships: MembershipResponse[]) {
    const csvContent = this.generateCSV(memberships);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `members_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  generateCSV(memberships: MembershipResponse[]): string {
    // Expand family memberships to individual members
    const expandedMembers = this.expandMembershipsToMembers(memberships);

    const headers = [
      'Member Name',
      'Email',
      'Phone',
      'Plan',
      'Coverage',
      'Status',
      'Payment Status',
      'Started',
      'Expires',
      'Owner Type',
      'Family Role'
    ];

    const rows = expandedMembers.map(m => [
      m.name,
      m.email,
      m.phone,
      m.plan,
      m.coverage,
      m.status,
      m.paymentStatus,
      this.formatDate(m.startedAt),
      this.formatDate(m.expiresAt),
      m.ownerType,
      m.familyRole || ''
    ]);

    const csvRows = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ];

    return csvRows.join('\n');
  }

  getMemberName(membership: MembershipResponse): string {
    if (membership.owner_user) {
      if (membership.owner_user.full_name) {
        return membership.owner_user.full_name;
      }
      if (membership.owner_user.first_name || membership.owner_user.last_name) {
        return `${membership.owner_user.first_name || ''} ${membership.owner_user.last_name || ''}`.trim();
      }
    }
    if (membership.family?.name) {
      return membership.family.name;
    }
    return `Member #${membership.membership_id}`;
  }

  getMemberEmail(membership: MembershipResponse): string {
    return membership.owner_user?.email || '';
  }

  getMemberPhone(membership: MembershipResponse): string {
    return membership.owner_user?.phone || '';
  }

  formatDate(date: Date | null): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}
